import { useSession as useBlitzSession } from "@blitzjs/auth"
import { useParam } from "@blitzjs/next"
import { useSession } from "app/authv2/use-session"
import { june } from "app/misc/june"
import LogRocket from "logrocket"
import posthog from "posthog-js"
import { useRouter } from "next/router"
import { useEffect, useState, useRef } from "react"
import { IntercomProps, useIntercom } from "react-use-intercom"
import { trpc } from "../trpcClient"

export const useIdentifyUser = () => {
  const router = useRouter()
  const { boot, update, trackEvent } = useIntercom()
  const { rawSession: newSession } = useSession()
  const { isLoading, ...session } = useBlitzSession({ suspense: false })

  const applicationId = useParam("applicationId", "number")!
  const { data: user } = trpc.user.legacyGetCurrentUser.useQuery(
    {},
    {
      suspense: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )
  const [isIdentified, setIsIdentified] = useState<boolean>(false)
  const [userData, setUserData] = useState<IntercomProps | null>(null)
  const referringUrlRef = useRef<string | null>(null)

  const { data: application } = trpc.applications.getApplication.useQuery(
    { applicationId },
    {
      suspense: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )

  const { data: billingData } = trpc.billing.getBillingData.useQuery(
    { applicationId },
    {
      suspense: false,
      enabled: !!applicationId,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )

  useEffect(() => {
    if (user && !user.isBotUser) {
      // This is an example script - don't forget to change it!
      LogRocket.identify(user.id.toString(), {
        email: user.email,
        ...(user.name ? { name: user.name } : {}),
        createdAt: user.createdAt.toISOString(), // needed for intercom to treat as date
        application_id: applicationId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (session && user && !user.isBotUser) {
      // @ts-ignore
      window.pylon = {
        chat_settings: {
          app_id: "3016d5e1-846d-4920-a1c0-7093acfdef82",
          email: user.email,
          name: user.name,
          account_id: `${process.env.NODE_ENV === "development" ? "dev_" : ""}${
            application?.organizationId || null
          }`,
          email_hash: newSession?.emailHash ?? undefined,
        },
      }

      // Note
      // ----
      // Intercom rate limits this to 20 times every 30 min. Make sure it doesnt
      // get called too often. Note that it gets sent everytime the user object changes
      // so don't add in properties that change hyper frequently (like 'last page
      // viewed' for example)

      const newUserData: IntercomProps = {
        userId: `${process.env.NODE_ENV === "development" ? "dev_" : ""}${
          user.id || session.userId || -1
        }`,
        email: user.email,
        name: user.name ? user.name : undefined,
        createdAt: user.createdAt.toISOString(), // needed for intercom to treat as date
        customAttributes: {
          web: true,
          web_role: user.role,
          web_nodeEnvironment: process.env.NODE_ENV,
          web_application_id: application ? applicationId : undefined,
          web_application_name: application ? application.name : undefined,
          web_has_payment_method: billingData
            ? billingData.paymentMethods
              ? billingData.paymentMethods.data.length > 0
              : false
            : false,
        },
        ...(application && {
          company: {
            companyId: `${process.env.NODE_ENV === "development" ? "dev_" : ""}${
              application.organizationId
            }`,
            name: application.organization.name,
            customAttributes: {
              web_trial_ends_at: application.organization.trialEndDate,
              web_has_payment_method: billingData
                ? billingData.paymentMethods
                  ? billingData.paymentMethods.data.length > 0
                  : false
                : false,
            },
          },
        }),
      }

      // Only update if there's a meaningful change (avoid expensive JSON.stringify)
      const hasDataChanged =
        !userData ||
        userData.userId !== newUserData.userId ||
        userData.email !== newUserData.email ||
        userData.name !== newUserData.name ||
        userData.company?.companyId !== newUserData.company?.companyId

      if (hasDataChanged) {
        setUserData(newUserData)
        if (!isIdentified) {
          boot(newUserData)
          setIsIdentified(true)
        } else {
          update(newUserData)
        }
        const { email, name, createdAt } = newUserData
        june.identify(user.id.toString(), {
          email,
          name,
          createdAt: createdAt?.toString(),
        })
        posthog.identify(user.id.toString(), {
          email,
          name,
          createdAt: createdAt?.toString(),
        })
        // @ts-ignore
        if (ko) {
          try {
            // @ts-ignore
            ko.identify({ email, name, createdAt: createdAt?.toString() })
          } catch (e) {}
        }

        if (application) {
          // @ts-ignore
          june.group(application.id.toString(), {
            ...(newUserData.company || {}),
          })
          posthog.group("organization", application.organizationId.toString(), {
            name: application.organization.name,
          })
        }
      }

      if (!isLoading && router.pathname !== referringUrlRef.current) {
        referringUrlRef.current = router.pathname
      }
    }
  }, [
    isLoading,
    session,
    application,
    user,
    billingData,
    router.pathname,
    isIdentified,
    boot,
    update,
    applicationId,
    trackEvent,
    newSession,
  ])
}
